/* eslint-disable react/jsx-props-no-spreading */
import { arrayOf, bool, func, node, oneOf, oneOfType, shape, string } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import YdtClose from '../../../assets/svg/closeIconNew.svg';
import YdtErrorFill from '../../../assets/icons/alert/error-fill.svg';
import YdtInfoFill from '../../../assets/icons/alert/info-fill.svg';
import YdtSuccess from '../../../assets/icons/alert/success.svg';
import Icon from '../../Icon';
import List from '../../List';
import { dt } from '../../../helpers/Utils/GetTailwindToken';

const MessageBox = ({
    automationId,
    className,
    closeHandler,
    listProps,
    message,
    messageType,
    shouldAnimate,
    showCloseButton,
    showMessage,
    showMessageTypeIcon,
}) => {
    const [shouldRender, setShouldRender] = useState(showMessage);

    const messageBoxRef = useRef(null);

    useEffect(() => {
        if (showMessage) setShouldRender(true);
        if (!showMessage && !shouldAnimate) setShouldRender(false);
        shouldRender && messageBoxRef.current.focus();
    }, [showMessage]);

    const onAnimationEnd = () => {
        if (!showMessage) setShouldRender(false);
    };

    const animationStyle = showMessage ? dt(['animate-animateIn']) : dt(['animate-animateOut']);

    const iconConfig = {
        height: '24',
        width: '24',
    };

    /**
     * Config is setup to add dynamic classes instead of using
     * template literals so that PurgeCSS can find the full
     * class names to prevent removing them.
     */
    const messageTypeConfig = {
        error: {
            children: <YdtErrorFill />,
            classes: {
                alignItems: dt(['items-center']),
                backgroundColor: dt(['bg-error-fill']),
                border: dt([
                    'border-0',
                    'border-transparent',
                    'border-l-4',
                    'border-l-error-border',
                ]),
                color: dt(['text-error-border']),
            },
        },
        info: {
            children: <YdtInfoFill />,
            classes: {
                alignItems: dt(['items-start']),
                backgroundColor: dt(['bg-info-fill']),
                border: dt([
                    'border-0',
                    'border-transparent',
                    'border-l-4',
                    'border-l-info-border',
                ]),
                color: dt(['text-info-border']),
            },
        },
        success: {
            children: <YdtSuccess />,
            classes: {
                alignItems: dt(['items-start']),
                backgroundColor: dt(['bg-success-fill']),
                border: dt([
                    'border-0',
                    'border-transparent',
                    'border-l-4',
                    'border-l-success-border',
                ]),
                color: dt(['text-success-border']),
            },
        },
    };
    const listpropsClass = ((listProps && listProps.children) || []).length > 0 ? 'mb-1' : '';
    const messageBoxConfig = {
        closeButton: {
            onClick: closeHandler,
            title: 'Close Message',
        },
        closeButtonIcon: {
            ...iconConfig,
            children: <YdtClose />,
        },
        content: {
            className: dt(['flex-auto', 'flex-col', 'pl-2', 'text-left', 'text-small']),
        },
        list: {
            ...listProps,
        },
        message: {
            className: `${dt(['text-normal', 'text-black', 'whitespace-normal', listpropsClass])}`,
        },
        messageTypeIcon: {
            ...iconConfig,
            children: messageTypeConfig[messageType].children,
            className: `${dt(['flex-none', 'self-start'])} ${
                messageTypeConfig[messageType].classes.color
            }`,
        },
        wrapper: {
            className: [
                className,
                `${messageTypeConfig[messageType].classes.alignItems}`,
                `${messageTypeConfig[messageType].classes.backgroundColor}`,
                `${messageTypeConfig[messageType].classes.border}`,
                `${dt([
                    'border',
                    'border-solid',
                    'shadow-elevation1',
                    'flex',
                    'p-2',
                    'rounded-sm',
                ])}`,
                `${shouldAnimate ? animationStyle : ''}`,
            ].join(' '),
            'data-automation-id': `${automationId}`,
            onAnimationEnd: shouldAnimate && onAnimationEnd,
            ref: messageBoxRef,
            tabIndex: '-1',
        },
    };

    return (
        shouldRender && (
            <div {...messageBoxConfig.wrapper}>
                {showMessageTypeIcon && <Icon {...messageBoxConfig.messageTypeIcon} />}
                <div {...messageBoxConfig.content}>
                    <p {...messageBoxConfig.message}>{message}</p>
                    {listProps && <List {...messageBoxConfig.list} />}
                </div>
                {showCloseButton && (
                    <button {...messageBoxConfig.closeButton} type="button">
                        <Icon {...messageBoxConfig.closeButtonIcon} />
                    </button>
                )}
            </div>
        )
    );
};

MessageBox.propTypes = {
    /** The message being displayed in the message box. */
    message: oneOfType([node, string]).isRequired,
    /** The message box type presented. */
    messageType: oneOf(['error', 'info', 'success']).isRequired,
    /** Automation testing ID. */
    automationId: string,
    /** Allows custom class(es) to be added on component container. */
    className: string,
    /** Close button behavior. */
    closeHandler: (props, propName, componentName) => {
        const isValid =
            props.showCloseButton && typeof props[propName] !== 'function'
                ? new Error(
                      `${componentName} prop '${propName}' must be defined
                as a function if 'showCloseButton' is set to 'true'.`.replace(/[\s]{2,}/, ' ')
                  )
                : null;

        return isValid;
    },
    /**
     * If passed this props object will be passed to the List component so that a
     * list of links or other items may be rendered according to the List component's
     * prerequisites.
     * */
    listProps: shape({
        /** Array of elements rendered as list items. */
        children: arrayOf([string, node]).isRequired,
        /** Automation testing ID. */
        automationId: string,
        /** Adds classes for list items. */
        itemClassName: string,
        /** Custom renderer function for list line items, must return a node. */
        itemRenderer: func,
        /** Adds classes to the list wrapper div. */
        listClassName: string,
        /**
         * Allows list to be ordered, bulleted, or unordered (none).
         * Default is unordered (none).
         */
        listType: oneOf(['decimal', 'disc', 'none']),
    }),
    /**
     * Animates transition of message box on show and hide
     * */
    shouldAnimate: bool,
    /**
     * Shows a close button allowing user to dismiss message box.
     * If showCloseButton is true then closeHandler must be passed
     * a function.
     */
    showCloseButton: bool,
    /** Show/hide the message. */
    showMessage: bool,
    /** Shows an icon associated with message type. */
    showMessageTypeIcon: bool,
};

MessageBox.defaultProps = {
    automationId: null,
    className: '',
    closeHandler: null,
    listProps: null,
    shouldAnimate: false,
    showCloseButton: false,
    showMessage: false,
    showMessageTypeIcon: true,
};

export default MessageBox;
